import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Clock,
  MapPin,
  Loader2
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function CalendarWidget({ expandable = true }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startD = new Date(year, month, 1);
      const endD = new Date(year, month + 1, 0);
      const startDate = `${startD.getFullYear()}-${String(startD.getMonth() + 1).padStart(2, '0')}-${String(startD.getDate()).padStart(2, '0')}`;
      const endDate = `${endD.getFullYear()}-${String(endD.getMonth() + 1).padStart(2, '0')}-${String(endD.getDate()).padStart(2, '0')}`;
      const data = await apiRequest(`/my-calendar/events?start_date=${startDate}&end_date=${endDate}`);
      setEvents(data || []);
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Get upcoming events (next 7 days)
  const today = new Date();
  const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  const upcomingEvents = events
    .filter(e => {
      const eventDate = new Date(e.start_time || e.date || e.start_date);
      return eventDate >= today && eventDate <= nextWeek;
    })
    .sort((a, b) => new Date(a.start_time || a.date || a.start_date) - new Date(b.start_time || b.date || b.start_date))
    .slice(0, 5);

  return (
    <Card data-testid="calendar-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-[#DC2626]" />
            <CardTitle className="text-lg">Calendar</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={prevMonth} className="h-8 w-8">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium min-w-[120px] text-center">{monthName}</span>
            <Button variant="ghost" size="icon" onClick={nextMonth} className="h-8 w-8">
              <ChevronRight className="h-4 w-4" />
            </Button>
            {expandable && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/calendar')}
                className="h-8 w-8 ml-2"
                title="Expand"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : upcomingEvents.length === 0 ? (
          <div className="text-center py-6 text-slate-400">
            <CalendarIcon className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No upcoming events</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-slate-500 font-medium">UPCOMING (Next 7 Days)</p>
            {upcomingEvents.map((event, idx) => {
              const eventDate = new Date(event.start_time || event.date || event.start_date);
              const isToday = eventDate.toDateString() === today.toDateString();
              
              return (
                <div
                  key={event.id || idx}
                  className={`p-3 rounded-lg border ${
                    isToday ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{event.title || event.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="h-3 w-3 text-slate-400" />
                        <span className="text-xs text-slate-500">
                          {isToday ? 'Today' : eventDate.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                          {event.start_time && !event.is_all_day && ` at ${new Date(event.start_time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`}
                        </span>
                      </div>
                    </div>
                    {isToday && (
                      <Badge className="bg-red-100 text-red-700 text-xs">Today</Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
